import { useEffect, useRef, useState } from 'react';
import { fetchWithCorpId, fetchWithCorpIdAndFormData, fetchBlobWithCorpId, asArray } from '../utils/api';
import { useAuth } from '../utils/auth';
import useServerTable from '../utils/useServerTable';
import Drawer from './Drawer';
import PageHeader from './ui/PageHeader';
import TableState from './ui/TableState';
import { FilterTh, SortableTh } from './ui/TableBits';
import { Field, Section } from './ui/formkit';
import InfoTip from './ui/InfoTip';
import { formatDate, toDateInputValue } from '../utils/dateUtils';
import { useToast } from './ui/Toast';
import { useConfirm } from './ui/ConfirmDialog';

const CATEGORIES = ['CONTRACT', 'IDENTIFICATION', 'CERTIFICATION', 'TAX', 'POLICY', 'OTHER'];

const categoryLabel = (value) => {
  if (!value) return '';
  return value.charAt(0) + value.slice(1).toLowerCase();
};

// Human readable file size: bytes under 1 KB, otherwise KB or MB.
const formatSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const MAX_FILE_BYTES = 15 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.docx', '.xlsx'];
const ACCEPT_ATTR = ALLOWED_EXTENSIONS.join(',');

// Formats an admin can require on a requirement (issue #122, backend #125).
// jpg accepts .jpeg files too; every other format maps to one extension.
const FORMAT_OPTIONS = ['pdf', 'png', 'jpg', 'docx', 'xlsx'];
const EXTENSIONS_BY_FORMAT = {
  pdf: ['.pdf'], png: ['.png'], jpg: ['.jpg', '.jpeg'], docx: ['.docx'], xlsx: ['.xlsx'],
};

// Normalize a requirement's allowedFormats (array or CSV from the API) to the
// known lowercase formats in canonical order. Empty means any supported type.
const parseFormats = (value) => {
  if (!value) return [];
  const raw = Array.isArray(value) ? value : String(value).split(',');
  const cleaned = raw.map(v => String(v).trim().toLowerCase());
  return FORMAT_OPTIONS.filter(f => cleaned.includes(f));
};

// "PDF", "PDF or JPG", "PDF, JPG or PNG" for restriction messages.
const formatOrText = (formats) => {
  const labels = formats.map(f => f.toUpperCase());
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
};

// "PDF, JPG" for list rows; "Any" when the requirement takes any format.
const formatsSummary = (value) => {
  const formats = parseFormats(value);
  return formats.length ? formats.map(f => f.toUpperCase()).join(', ') : 'Any';
};

// Compact employee-facing restriction: "PDF only" or "PDF or JPG". Null when
// the requirement takes any format, so nothing renders.
const formatRestrictionText = (value) => {
  const formats = parseFormats(value);
  if (!formats.length) return null;
  return formats.length === 1 ? `${formats[0].toUpperCase()} only` : formatOrText(formats);
};

const fileError = (file, allowedFormats = []) => {
  const name = (file.name || '').toLowerCase();
  const extensions = allowedFormats.length
    ? allowedFormats.flatMap(f => EXTENSIONS_BY_FORMAT[f])
    : ALLOWED_EXTENSIONS;
  if (!extensions.some(ext => name.endsWith(ext))) {
    if (allowedFormats.length === 1) {
      return `This document must be ${allowedFormats[0] === 'xlsx' ? 'an' : 'a'} ${formatOrText(allowedFormats)}.`;
    }
    if (allowedFormats.length > 1) {
      return `This document must be ${formatOrText(allowedFormats)}.`;
    }
    return 'This file type is not supported. Use PDF, PNG, JPG, JPEG, DOCX, or XLSX.';
  }
  if (file.size > MAX_FILE_BYTES) {
    return 'This file is larger than 15 MB. Choose a smaller file.';
  }
  return null;
};

// Requirement fulfillment status shown to employees and on emp-targeted rows.
const statusBadge = (status) => {
  if (!status) return null;
  return status === 'Submitted'
    ? <span className="badge badge-success">Submitted</span>
    : <span className="badge badge-warning">Needed</span>;
};

// Upload form rendered inside the drawer. Staff can attach the document to an
// employee; employee uploads are always their own (the backend enforces this).
// When `requirement` is set (an employee uploading against a posted
// requirement) the category is locked to the requirement's and the upload is
// linked back to it via requirementId.
function DocumentUploadForm({ isStaff, employees, requirement, formRef, onSuccess }) {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState(requirement?.category || 'OTHER');
  const [description, setDescription] = useState('');
  const [empId, setEmpId] = useState('');
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Requirement-scoped format restriction (issue #122). Free uploads and
  // requirements without allowedFormats keep the full supported list.
  const allowedFormats = parseFormats(requirement?.allowedFormats);
  const acceptAttr = allowedFormats.length
    ? allowedFormats.flatMap(f => EXTENSIONS_BY_FORMAT[f]).join(',')
    : ACCEPT_ATTR;

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0] || null;
    if (!selected) {
      setFile(null);
      return;
    }
    const error = fileError(selected, allowedFormats);
    if (error) {
      setMessage(error);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setMessage('');
    setFile(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploading) return;
    if (!file) {
      setMessage('Choose a file to upload.');
      return;
    }
    setMessage('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      if (description.trim()) fd.append('description', description.trim());
      if (isStaff && empId) fd.append('empId', empId);
      if (requirement) fd.append('requirementId', requirement.id);
      await fetchWithCorpIdAndFormData('/documents', fd, { method: 'POST' });
      toast.success('Document uploaded.');
      onSuccess?.();
    } catch (error) {
      setMessage(error.message || 'Failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const isDirty = Boolean(file || description);

  return (
    <form ref={formRef} onSubmit={handleSubmit} data-dirty={isDirty ? 'true' : 'false'}>
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>{message}</div>
      )}
      {requirement && (
        <div style={{
          fontFamily: 'var(--font-sans)', fontSize: '13px', color: 'var(--muted)',
          marginBottom: '20px',
        }}>
          For: {requirement.title}
        </div>
      )}
      <Section
        title="File"
        description={allowedFormats.length
          ? `${formatOrText(allowedFormats)} up to 15 MB.`
          : 'PDF, PNG, JPG, JPEG, DOCX, or XLSX up to 15 MB.'}
        grid="1fr"
      >
        <Field
          label="Document"
          required
          hint={requirement ? `Your file will be saved as ${requirement.title}.` : undefined}
        >
          <input ref={fileInputRef} type="file" accept={acceptAttr} onChange={handleFileSelect} required />
          {file && (
            <span style={{ marginTop: '8px', fontSize: '13px', color: 'var(--muted)' }}>
              {file.name} ({formatSize(file.size)})
            </span>
          )}
        </Field>
      </Section>
      <Section title="Details" description="Categorize the document so it is easy to find." grid="1fr">
        <Field label="Category" required hint={requirement ? 'Set by the requirement.' : undefined}>
          <select value={category} onChange={e => setCategory(e.target.value)} required disabled={Boolean(requirement)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
          </select>
        </Field>
        <Field label="Description">
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
        </Field>
        {isStaff && (
          <Field label="Employee">
            <select value={empId} onChange={e => setEmpId(e.target.value)}>
              <option value="">Corporation (no employee)</option>
              {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.fullName}</option>)}
            </select>
          </Field>
        )}
      </Section>
    </form>
  );
}

// Create/edit form for a document requirement, rendered inside the drawer.
// Admin/super only; edit prefills from the list row (the list endpoint
// already carries every editable field).
function RequirementForm({ requirement, employees, formRef, onSuccess }) {
  const toast = useToast();
  const isEdit = Boolean(requirement);
  const [form, setForm] = useState({
    title: requirement?.title || '',
    category: requirement?.category || 'OTHER',
    instructions: requirement?.instructions || '',
    dueDate: toDateInputValue(requirement?.dueDate),
    empId: requirement?.empId != null ? String(requirement.empId) : '',
    active: isEdit ? Boolean(requirement.active) : true,
    allowedFormats: parseFormats(requirement?.allowedFormats),
  });
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  // Snapshot of the initial state; drives the drawer's discard guard.
  const initialSnapshotRef = useRef(null);
  if (initialSnapshotRef.current === null) initialSnapshotRef.current = JSON.stringify(form);
  const isDirty = JSON.stringify(form) !== initialSnapshotRef.current;

  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  // Keep the array in FORMAT_OPTIONS order so the dirty snapshot stays stable.
  const toggleFormat = (fmt) => setForm(prev => ({
    ...prev,
    allowedFormats: prev.allowedFormats.includes(fmt)
      ? prev.allowedFormats.filter(f => f !== fmt)
      : FORMAT_OPTIONS.filter(f => prev.allowedFormats.includes(f) || f === fmt),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage('');
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        category: form.category,
        instructions: form.instructions.trim() || null,
        dueDate: form.dueDate || null,
        empId: form.empId ? Number(form.empId) : null,
        active: form.active,
        allowedFormats: form.allowedFormats,
      };
      await fetchWithCorpId(isEdit ? `/documents/requirements/${requirement.id}` : '/documents/requirements', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      toast.success(isEdit ? 'Requirement updated.' : 'Requirement created.');
      onSuccess?.();
    } catch (error) {
      setMessage(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} data-dirty={isDirty ? 'true' : 'false'}>
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>{message}</div>
      )}
      <Section title="Requirement" description="What employees must provide." grid="1fr">
        <Field label="Title" required>
          <input type="text" value={form.title} onChange={e => setField('title', e.target.value)} required />
        </Field>
        <Field label="Category" required>
          <select value={form.category} onChange={e => setField('category', e.target.value)} required>
            {CATEGORIES.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
          </select>
        </Field>
        {/* Not a formkit Field: Field renders a <label>, and the five checkbox
            labels inside it would nest labels. Same pattern as Active below. */}
        <div>
          <span style={{
            display: 'block',
            fontFamily: 'var(--font-sans)',
            fontSize: '12px', fontWeight: 500, color: 'var(--muted)',
            letterSpacing: '1.2px', textTransform: 'uppercase',
            marginBottom: '8px',
          }}>
            Allowed formats
            <InfoTip tip="Which file types fulfill this requirement. Leave all unchecked to accept any supported type." />
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            {FORMAT_OPTIONS.map(fmt => (
              <label
                key={fmt}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  fontFamily: 'var(--font-sans)', fontSize: '13px',
                  color: 'var(--ink)', fontWeight: 400,
                }}
              >
                <input
                  type="checkbox"
                  checked={form.allowedFormats.includes(fmt)}
                  onChange={() => toggleFormat(fmt)}
                  style={{ width: 'auto', height: 'auto', margin: 0 }}
                />
                {fmt.toUpperCase()}
              </label>
            ))}
          </div>
        </div>
        <Field label="Instructions" hint="Shown to employees under the requirement.">
          <textarea value={form.instructions} onChange={e => setField('instructions', e.target.value)} rows={3} placeholder="Optional" />
        </Field>
      </Section>
      <Section title="Scope" description="Who must provide it and by when." grid="1fr">
        <Field label="Applies to">
          <select value={form.empId} onChange={e => setField('empId', e.target.value)}>
            <option value="">All employees</option>
            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.fullName}</option>)}
          </select>
        </Field>
        <Field label="Due date">
          <input type="date" value={form.dueDate} onChange={e => setField('dueDate', e.target.value)} />
        </Field>
        {isEdit && (
          <div>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              fontFamily: 'var(--font-sans)', fontSize: '13px',
              color: 'var(--ink)', fontWeight: 400,
            }}>
              <input
                type="checkbox"
                checked={form.active}
                onChange={e => setField('active', e.target.checked)}
                style={{ width: 'auto', height: 'auto', margin: 0 }}
              />
              Active
            </label>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: '12.5px', color: 'var(--muted-soft)', marginTop: '6px' }}>
              Inactive requirements are hidden from employees.
            </div>
          </div>
        )}
      </Section>
    </form>
  );
}

// One key per filterable column (issue #144); each maps to a same-named
// query param on GET /documents (backend #131).
const EMPTY_FILTER = { filename: '', category: '', empId: '', uploadedBy: '' };

function DocumentsTable() {
  const toast = useToast();
  const confirm = useConfirm();
  const { getUserRoles, hasRole, isSuperUser } = useAuth();
  const roles = getUserRoles();
  const isStaff = roles.some(r => ['admin', 'employer', 'super'].includes(r));
  // Employers see requirement progress read-only; only admin/super manage.
  const canManage = hasRole('admin') || isSuperUser();

  const {
    rows: documents, loading, error,
    filter, handleFilterChange, clearFilters, hasActiveFilters,
    sortConfig, handleSort,
    page, setPage, size, handleChangePageSize,
    totalElements, totalPages, reload,
  } = useServerTable('/documents', { emptyFilter: EMPTY_FILTER, initialSort: { key: 'createdAt', direction: 'desc' } });
  const [employees, setEmployees] = useState([]);

  // Requirements layer: the employee checklist and the staff progress list
  // share one fetch of GET /documents/requirements (the backend scopes rows
  // by role).
  const [requirements, setRequirements] = useState([]);
  const [reqLoading, setReqLoading] = useState(true);
  const [reqError, setReqError] = useState(null);
  const [reqReloadKey, setReqReloadKey] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Set when an employee uploads against a requirement; locks the category
  // and links the upload via requirementId.
  const [uploadRequirement, setUploadRequirement] = useState(null);
  const formRef = useRef(null);
  const openUpload = () => { setUploadRequirement(null); setDrawerOpen(true); };
  const openRequirementUpload = (req) => { setUploadRequirement(req); setDrawerOpen(true); };
  const closeDrawer = () => setDrawerOpen(false);
  const handleUploadSuccess = () => {
    closeDrawer();
    reload();
    if (uploadRequirement) setReqReloadKey(k => k + 1);
  };

  // Requirement create/edit drawer (admin/super only).
  const [reqDrawer, setReqDrawer] = useState(null);
  const reqFormRef = useRef(null);
  const openReqCreate = () => setReqDrawer({ mode: 'create' });
  const openReqEdit = (req) => setReqDrawer({ mode: 'edit', requirement: req });
  const closeReqDrawer = () => setReqDrawer(null);
  const handleReqSuccess = () => { closeReqDrawer(); setReqReloadKey(k => k + 1); };

  // Staff filter documents by employee; employees only ever see their own.
  useEffect(() => {
    if (!isStaff) return;
    fetchWithCorpId('/employees?page=0&size=99999&sort=fullName')
      .then(data => setEmployees(asArray(data)))
      .catch(() => {});
  }, [isStaff]);

  useEffect(() => {
    let cancelled = false;
    setReqLoading(true);
    fetchWithCorpId('/documents/requirements')
      .then(data => {
        if (cancelled) return;
        setRequirements(asArray(data));
        setReqError(null);
      })
      .catch((e) => { if (!cancelled) { setReqError(e.message || 'Failed to load requirements'); setRequirements([]); } })
      .finally(() => { if (!cancelled) setReqLoading(false); });
    return () => { cancelled = true; };
  }, [reqReloadKey]);

  const handleDownload = async (doc) => {
    try {
      const blob = await fetchBlobWithCorpId(`/documents/${doc.id}/download`);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch (e) {
      toast.error(e.message || 'Failed to open document.');
    }
  };

  const handleDelete = async (doc) => {
    const ok = await confirm({
      title: 'Delete document',
      message: `This removes "${doc.filename}" permanently. This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await fetchWithCorpId(`/documents/${doc.id}`, { method: 'DELETE' });
      toast.success('Document deleted.');
      reload();
    } catch (e) {
      toast.error(e.message || 'Failed to delete document.');
    }
  };

  // The backend rejects deletion while uploads reference the requirement;
  // surface that message as-is.
  const handleReqDelete = async (req) => {
    const ok = await confirm({
      title: 'Delete requirement',
      message: `This removes "${req.title}". This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await fetchWithCorpId(`/documents/requirements/${req.id}`, { method: 'DELETE' });
      toast.success('Requirement deleted.');
      setReqReloadKey(k => k + 1);
    } catch (e) {
      toast.error(e.message || 'Failed to delete requirement.');
    }
  };

  const columnCount = isStaff ? 7 : 6;

  const iconBtnStyle = {
    background: 'none',
    border: '1px solid var(--hairline)',
    borderRadius: '6px',
    padding: '5px 8px',
    cursor: 'pointer',
    color: 'var(--muted)',
    display: 'inline-flex',
    alignItems: 'center',
  };

  return (
    <div className="page-container">
      <PageHeader
        title="Documents"
        subtitle={isStaff ? 'All documents for this corporation' : 'Your documents'}
        actions={
          <button className="btn-primary" onClick={openUpload}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Upload document
          </button>
        }
      />

      {/* Employee checklist: what the corp requires of this employee. Hidden
          entirely when nothing applies. */}
      {!isStaff && (requirements.length > 0 || reqError) && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header">Required documents</div>
          {reqError ? (
            <div style={{ padding: '20px 24px' }}>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', color: 'var(--danger)', marginBottom: '12px' }}>
                {reqError}
              </div>
              <button type="button" className="btn-secondary" onClick={() => setReqReloadKey(k => k + 1)}>
                Try again
              </button>
            </div>
          ) : requirements.map((req, i) => (
            <div
              key={req.id}
              style={{
                padding: '16px 24px',
                borderBottom: i === requirements.length - 1 ? 'none' : '1px solid var(--hairline-soft)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                gap: '16px', flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: '220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', fontWeight: 500, color: 'var(--ink)' }}>
                    {req.title}
                  </span>
                  <span className="badge badge-neutral">{categoryLabel(req.category)}</span>
                  {formatRestrictionText(req.allowedFormats) && (
                    <span style={{ fontFamily: 'var(--font-sans)', fontSize: '12.5px', color: 'var(--muted)' }}>
                      {formatRestrictionText(req.allowedFormats)}
                    </span>
                  )}
                </div>
                {req.instructions && (
                  <div style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', color: 'var(--muted)', marginTop: '6px' }}>
                    {req.instructions}
                  </div>
                )}
                {req.dueDate && (
                  <div style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
                    Due {formatDate(req.dueDate)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                {statusBadge(req.status)}
                {req.status === 'Submitted' && req.documentFilename && (
                  <span
                    title={req.documentFilename}
                    style={{
                      fontFamily: 'var(--font-sans)', fontSize: '13px', color: 'var(--muted)',
                      maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {req.documentFilename}
                  </span>
                )}
                {req.status !== 'Submitted' && (
                  <button type="button" className="btn-secondary" onClick={() => openRequirementUpload(req)}>
                    Upload
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Staff view: requirement list with progress. Employers read-only;
          admin/super manage. */}
      {isStaff && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header" style={{ justifyContent: 'space-between' }}>
            <span>Requirements</span>
            {canManage && (
              <button type="button" className="btn-secondary" onClick={openReqCreate}>
                New requirement
              </button>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" aria-busy={reqLoading}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Formats</th>
                  <th>Applies to</th>
                  <th>Due date</th>
                  <th>Progress</th>
                  {canManage && <th>Actions</th>}
                </tr>
              </thead>
              <tbody style={{ opacity: reqLoading && requirements.length > 0 ? 0.55 : 1 }}>
                {requirements.length === 0 ? (
                  <TableState
                    colSpan={canManage ? 7 : 6}
                    loading={reqLoading}
                    error={reqError}
                    onRetry={() => setReqReloadKey(k => k + 1)}
                    emptyTitle="No requirements yet"
                    emptyHint={canManage
                      ? 'Post a requirement to tell employees which documents to upload.'
                      : 'Requirements posted by admins will show here.'}
                    emptyAction={canManage ? { label: 'New requirement', onClick: openReqCreate } : undefined}
                  />
                ) : requirements.map(req => (
                  <tr key={req.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 500 }}>{req.title}</span>
                        {!req.active && <span className="badge badge-neutral">Inactive</span>}
                      </div>
                    </td>
                    <td><span className="badge badge-neutral">{categoryLabel(req.category)}</span></td>
                    <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatsSummary(req.allowedFormats)}</td>
                    <td>{req.empId != null ? req.employeeName : 'All employees'}</td>
                    <td style={{ color: 'var(--muted)', fontSize: '13px' }}>
                      {req.dueDate ? formatDate(req.dueDate) : '—'}
                    </td>
                    <td>
                      {req.empId != null ? statusBadge(req.status) : (
                        <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                          {`${req.satisfiedCount ?? 0} of ${req.employeeCount ?? 0} submitted`}
                        </span>
                      )}
                    </td>
                    {canManage && (
                      <td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            type="button"
                            onClick={() => openReqEdit(req)}
                            title="Edit"
                            aria-label={`Edit ${req.title}`}
                            style={iconBtnStyle}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReqDelete(req)}
                            title="Delete"
                            aria-label={`Delete ${req.title}`}
                            style={{ ...iconBtnStyle, color: 'var(--danger)' }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <button className="btn-secondary" onClick={clearFilters}>Clear filters</button>
      </div>

      <div className="table-wrapper">
        <table className="data-table" aria-busy={loading}>
          <thead>
            <tr>
              <SortableTh col="filename" label="Name" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="category" label="Category" sortConfig={sortConfig} onSort={handleSort} />
              {isStaff && <th>Employee</th>}
              <SortableTh col="uploadedBy" label="Uploaded by" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="createdAt" label="Date" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="sizeBytes" label="Size" sortConfig={sortConfig} onSort={handleSort} />
              <th>Actions</th>
            </tr>
            <tr>
              <FilterTh label="Name" name="filename" value={filter.filename} onChange={handleFilterChange} />
              <FilterTh
                label="Category"
                name="category"
                value={filter.category}
                onChange={handleFilterChange}
                options={CATEGORIES.map(c => ({ value: c, label: categoryLabel(c) }))}
              />
              {isStaff && (
                <FilterTh
                  label="Employee"
                  name="empId"
                  value={filter.empId}
                  onChange={handleFilterChange}
                  options={employees.map(emp => ({ value: String(emp.id), label: emp.fullName }))}
                />
              )}
              <FilterTh label="Uploaded by" name="uploadedBy" value={filter.uploadedBy} onChange={handleFilterChange} />
              <FilterTh />
              <FilterTh />
              <FilterTh />
            </tr>
          </thead>
          <tbody style={{ opacity: loading && documents.length > 0 ? 0.55 : 1 }}>
            {documents.length === 0 ? (
              <TableState
                colSpan={columnCount}
                loading={loading}
                error={error}
                onRetry={reload}
                filtered={hasActiveFilters}
                onClearFilters={clearFilters}
                emptyTitle="No documents yet"
                emptyHint="Upload contracts, IDs, and other files to keep them in one place."
                emptyAction={{ label: 'Upload document', onClick: openUpload }}
              />
            ) : documents.map(doc => (
              <tr key={doc.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button type="button" className="btn-link table-link" onClick={() => handleDownload(doc)} title="Open document">
                      {doc.filename}
                    </button>
                    {doc.requirementTitle && (
                      <span
                        className="badge badge-info"
                        title={doc.requirementTitle}
                        style={{
                          maxWidth: '180px', display: 'inline-block',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {doc.requirementTitle}
                      </span>
                    )}
                  </div>
                </td>
                <td><span className="badge badge-neutral">{categoryLabel(doc.category)}</span></td>
                {isStaff && (
                  <td style={{ fontWeight: '500' }}>
                    {doc.empId != null ? doc.employeeName : 'Corporation'}
                  </td>
                )}
                <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{doc.uploadedBy}</td>
                <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(doc.createdAt)}</td>
                <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatSize(doc.sizeBytes)}</td>
                <td>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={() => handleDownload(doc)}
                      title="Download"
                      aria-label={`Download ${doc.filename}`}
                      style={iconBtnStyle}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(doc)}
                      title="Delete"
                      aria-label={`Delete ${doc.filename}`}
                      style={{ ...iconBtnStyle, color: 'var(--danger)' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <span style={{ fontSize: '13px', color: 'var(--muted)', marginRight: 'auto' }}>
            {typeof totalElements === 'number' ? `${totalElements} total` : ''}
          </span>
          <label style={{ fontSize: '13px', color: 'var(--muted)' }}>Rows:&nbsp;
            <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 6px' }}>
              {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
          <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
        </div>
      </div>

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        title="Upload document"
        subtitle={uploadRequirement
          ? 'Upload the document this requirement asks for.'
          : "Add a file to this corporation's document vault."}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => formRef.current?.requestSubmit()}>
              Upload
            </button>
          </>
        }
      >
        {drawerOpen && (
          <DocumentUploadForm
            isStaff={isStaff}
            employees={employees}
            requirement={uploadRequirement}
            formRef={formRef}
            onSuccess={handleUploadSuccess}
          />
        )}
      </Drawer>

      <Drawer
        open={Boolean(reqDrawer)}
        onClose={closeReqDrawer}
        title={reqDrawer?.mode === 'edit' ? 'Edit requirement' : 'New requirement'}
        subtitle="Tell employees which document to provide."
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeReqDrawer}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => reqFormRef.current?.requestSubmit()}>
              Save
            </button>
          </>
        }
      >
        {reqDrawer && (
          <RequirementForm
            requirement={reqDrawer.mode === 'edit' ? reqDrawer.requirement : null}
            employees={employees}
            formRef={reqFormRef}
            onSuccess={handleReqSuccess}
          />
        )}
      </Drawer>
    </div>
  );
}

export default DocumentsTable;
